import { memo, useState, useCallback, useMemo, lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from '../checkListV2.module.scss';
import cn from 'classnames';
import { TouchableButton, TouchableDiv } from '@components/common/Touchable';
import { ReactComponent as MoreIcon } from '@assets/images/moreInfo.svg';
import { ReactComponent as MoreVertical } from '@assets/images/moreVertical.svg';
import { useReceiptModal } from './customHooks/useReceiptModal';
import { getHoldTimerState } from '@utility/helperFunction';
import CustomEventEmitter from '@utility/customEventEmitter';


const TableRow = ({
  row = {},
  gridStyle = '',
  serverAppTheme = {},
  tableRowActionButtons = [],
  onOrderClick = () => {},
  onPayClick = () => {},
  onToolsClick = () => {},
  isActiveRow = false,
  isToolsOpen = false,
  setActiveRow = () => {},
  setSeat = () => {},
  onOpenPrint = () => {},
  isOnlyOrderMode = false,
}) => {
  const { onOpenReceipt, jsx: receiptModalJSX } = useReceiptModal({ row, serverAppTheme });

  const receiptTypes = useSelector(
    state => state.oauth?.deviceInformation?.deviceGroup?.receiptTypes ?? null
  );

  const canPrint = !!receiptTypes?.print;

  const clickHandler = {
    order: data => onOrderClick(data),
    pay: data => onPayClick(data),
    tools: () => {
      CustomEventEmitter.dispatchEvent('closeOrderSendMode');
      onToolsClick(row);
    },
    print: () => onOpenPrint(row),
    receipt: () => onOpenReceipt(),
  };

  const isPaid = !row?.open;

  const buttonVisibilityHandler = {
    order: !isPaid && !isOnlyOrderMode,
    pay: !isPaid,
    receipt: isPaid,
    print: isPaid && canPrint,
    tools: true,
  };

  const buttonIcons = {
    tools: {
      img: isToolsOpen ? (
        <MoreVertical fill={serverAppTheme?.primary_text} />
      ) : (
        <MoreIcon fill={serverAppTheme?.btn_font_color} />
      ),
    },
  };

  const isReceiptDisabled = useMemo(
    () => (row?.tickets?.filter(e => e.ticketStatusId === 1).length ?? 0) === 0,
    [row?.tickets]
  );

  const buttonDisableHanlder = useMemo(
    () => ({
      pay: row?.unpaidAmount === 0,
      receipt: isReceiptDisabled,
      order: row?.paidAmount > 0 && row.totalAmount === row.paidAmount &&
    Number(row.unpaidAmount) === 0
    }),
    [row?.unpaidAmount, isReceiptDisabled]
  );

  const hasPendingItems = useMemo(() => row?.PendingItems?.length > 0, [row?.PendingItems]);
  const isHeldItem = useMemo(
    () => row?.PendingItems?.some(item => item?.sendDate != null && item?.sendDate !== '') ?? false,
    [row?.PendingItems]
  );

  const [hasLapsedHold, setHasLapsedHold] = useState(false);

  useEffect(() => {
    if (!row?.PendingItems?.length) {
      setHasLapsedHold(false);
      return;
    }

    const checkLapsed = () => {
      return row.PendingItems.some(item => {
        if (!item.sendDate) return false;
        if (item.sendingDate) return true;
        const state = getHoldTimerState(item.sendDate);
        return state && !state.isPositive;
      });
    };

    setHasLapsedHold(checkLapsed());

    let intervalId;
    const timeoutId = setTimeout(() => {
      setHasLapsedHold(prev => {
        const current = checkLapsed();
        return prev !== current ? current : prev;
      });
      intervalId = setInterval(() => {
        setHasLapsedHold(prev => {
          const current = checkLapsed();
          if (prev !== current) return current;
          return prev;
        });
      }, 1000);
    }, 1000 - (Date.now() % 1000));

    return () => { clearTimeout(timeoutId); clearInterval(intervalId); };
  }, [row?.PendingItems]);

  return (
    <>
      <TouchableDiv
        className={cn(
          styles.table_row_container,
          'my-1 px-2 rounded fw-500 fs_16'
        )}
        style={{
          ...gridStyle,
          border: isActiveRow ? serverAppTheme?.select_border : undefined,
          backgroundColor: isActiveRow
            ? serverAppTheme?.card_bg
            : serverAppTheme?.tile_bg,
        }}
        onClick={e => {
          e.preventDefault();
          if (isActiveRow) return;
          setActiveRow(row?.id);
          setSeat(row);
        }}
      >
        <div className="px-0">
          <span
            className={cn(
              'col-11 p-1 rounded text-truncate d-flex align-items-center justify-content-center'
            )}
            style={{
              backgroundColor: (isHeldItem && !hasLapsedHold)
                ? 'rgb(255, 225, 204)' : (isHeldItem && hasLapsedHold) ? 'rgb(245, 176, 176)'
                  : hasPendingItems
                    ? 'rgb(247, 229, 158)'
                    : serverAppTheme?.seat_bg,
              color: hasPendingItems || hasLapsedHold ? '#000' : serverAppTheme?.seat_box_text,
            }}
          >
            {/* {row.name ?? row.stationName} */}
            {row?.tab ? row?.name : row?.stationNumber}
          </span>
        </div>
        <div className="p-1 text-truncate d-flex align-items-center">
          {row.number}
        </div>
        <div className="d-flex align-items-center">
          {`$${(row?.paidAmount ?? 0).toFixed(2)}`}
        </div>
        <div className="d-flex align-items-center">
          {`$${(row?.unpaidAmount ?? 0).toFixed(2)}`}
        </div>
        <div className="d-flex">
          {isActiveRow &&
            tableRowActionButtons.map(button =>
              buttonVisibilityHandler[button.key] ? (
                <TouchableButton
                  key={button.key}
                  className={cn(
                    styles.flexItem_center,
                    'd-flex mr-1 justify-content-center btn35 border-0 text-center btn-ripple w-100 p-0 m-0'
                  )}
                  style={
                    isPaid && button.key !== 'tools'
                      ? {
                        background:
                          button.key === 'receipt' && isReceiptDisabled
                            ? serverAppTheme?.primary_btn_disable
                            : serverAppTheme?.primaryBtn,
                        color:
                          button.key === 'receipt' && isReceiptDisabled
                            ? serverAppTheme?.btn_font_color_disabled
                            : serverAppTheme?.btn_font_color,
                        fontSize: '16px',
                        fontWeight: 500,
                      }
                      : (button.key === 'pay' && buttonDisableHanlder.pay) || (button.key === 'order' && buttonDisableHanlder.order)
                        ? {
                          background: serverAppTheme?.disabledPayBtn,
                          color: serverAppTheme?.btn_font_color_disabled,
                        }
                        : {
                          ...button.style,
                          color: serverAppTheme?.btn_font_color,
                        }
                  }
                  disabled={buttonDisableHanlder[button.key]}
                  onClick={() => clickHandler[button.key]?.(row)}
                >
                  {buttonIcons[button['key']]?.img ?? button.name}
                </TouchableButton>
              ) : (
                <></>
              )
            )}
        </div>
      </TouchableDiv>
      {receiptModalJSX}
    </>
  );
};

const areEqual = (prev, next) =>
  prev.isActiveRow === next.isActiveRow &&
  prev.isToolsOpen === next.isToolsOpen &&
  prev.row.id === next.row.id &&
  prev.row.name === next.row.name &&
  prev.row.number === next.row.number &&
  prev.row.open === next.row.open &&
  prev.row.paidAmount === next.row.paidAmount &&
  prev.row.unpaidAmount === next.row.unpaidAmount &&
  prev.row.PendingItems === next.row.PendingItems &&
  prev.serverAppTheme === next.serverAppTheme &&
  prev.gridStyle === next.gridStyle &&
  prev.tableRowActionButtons === next.tableRowActionButtons &&
  prev.onOrderClick === next.onOrderClick &&
  prev.onPayClick === next.onPayClick &&
  prev.onToolsClick === next.onToolsClick &&
  prev.setActiveRow === next.setActiveRow &&
  prev.setSeat === next.setSeat &&
  prev.onOpenPrint === next.onOpenPrint;

export default memo(TableRow, areEqual);
